import React from 'react';
import { CheckCircle, XCircle, Coins, X, Loader2 } from 'lucide-react';

/**
 * CreditActionModal - Shows confirmation for booking/cancellation with credit change
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close the modal
 * @param {'success' | 'error' | 'confirm' | 'loading'} props.type - Type of modal
 * @param {'booking' | 'cancellation'} props.action - The action being confirmed
 * @param {string} props.lessonTitle - Title of the lesson
 * @param {number} props.creditsBefore - Credit balance before action
 * @param {number} props.creditsAfter - Credit balance after action
 * @param {string} [props.errorMessage] - Error message if type is 'error'
 * @param {function} [props.onConfirm] - Callback for confirm button (for confirm type)
 */
const CreditActionModal = ({
  isOpen,
  onClose,
  type = 'success',
  action = 'booking',
  lessonTitle = '',
  creditsBefore = 0,
  creditsAfter = 0,
  errorMessage = '',
  onConfirm,
}) => {
  if (!isOpen) return null;

  const isBooking = action === 'booking';
  const isIndividualCancellation = action === 'individual_cancellation';
  const isSuccess = type === 'success';
  const isConfirm = type === 'confirm';
  const isLoading = type === 'loading';

  const getTitle = () => {
    if (type === 'error') return 'Error';
    if (isLoading) {
      if (isIndividualCancellation) return 'Processing Refund...';
      return isBooking ? 'Registering...' : 'Cancelling...';
    }
    if (isConfirm) {
      if (isIndividualCancellation) return 'Cancel & Refund';
      return isBooking ? 'Confirm Booking' : 'Confirm Cancellation';
    }
    if (isIndividualCancellation) return 'Refund Complete!';
    return isBooking ? 'Booking Confirmed!' : 'Cancellation Confirmed!';
  };

  const getMessage = () => {
    if (type === 'error') return errorMessage;
    if (isLoading) {
      if (isIndividualCancellation) return `Cancelling and refunding "${lessonTitle}"`;
      return isBooking ? `Registering for "${lessonTitle}"` : `Cancelling registration for "${lessonTitle}"`;
    }
    if (isConfirm) {
      if (isIndividualCancellation) {
        return `Cancel your private lesson "${lessonTitle}"? Your payment will be refunded.`;
      }
      return isBooking
        ? `Use 1 credit to register for "${lessonTitle}"?`
        : `Cancel your registration for "${lessonTitle}"? Your credit will be refunded.`;
    }
    if (isIndividualCancellation) {
      return `Your session "${lessonTitle}" has been cancelled and refunded.`;
    }
    return isBooking
      ? `You're registered for "${lessonTitle}"`
      : `Your registration for "${lessonTitle}" has been cancelled.`;
  };

  const getSubMessage = () => {
    if (isSuccess && isBooking) {
      return 'Return to this page when the lesson starts to join.';
    }
    return null;
  };

  const subMessage = getSubMessage();

  const creditsChanged = creditsAfter - creditsBefore;

  return (
    <div className="credit-modal-overlay" onClick={isLoading ? undefined : onClose}>
      <div className="credit-modal" onClick={(e) => e.stopPropagation()}>
        {!isLoading && (
          <button className="credit-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        )}

        <div className={`credit-modal-icon ${type}`}>
          {type === 'error' ? (
            <XCircle size={48} />
          ) : isLoading ? (
            <Loader2 size={48} className="spinning" />
          ) : (
            <CheckCircle size={48} />
          )}
        </div>

        <h2 className="credit-modal-title">{getTitle()}</h2>
        <p className={`credit-modal-message ${subMessage ? 'has-submessage' : ''}`}>{getMessage()}</p>
        {subMessage && <p className="credit-modal-submessage">{subMessage}</p>}

        {type !== 'error' && type !== 'loading' && !isIndividualCancellation && (
          <div className="credit-modal-balance">
            <div className="balance-change">
              <div className="balance-item before">
                <Coins size={18} />
                <span>{creditsBefore}</span>
              </div>
              <span className="balance-arrow">→</span>
              <div className="balance-item after">
                <Coins size={18} />
                <span>{creditsAfter}</span>
              </div>
            </div>
            <p className="balance-label">
              {isConfirm
                ? `${creditsChanged > 0 ? '+' : ''}${creditsChanged} credit${Math.abs(creditsChanged) !== 1 ? 's' : ''}`
                : `You now have ${creditsAfter} credit${creditsAfter !== 1 ? 's' : ''}`
              }
            </p>
          </div>
        )}

        {!isLoading && (
          <div className="credit-modal-actions">
            {isConfirm ? (
              <>
                <button className="modal-btn secondary" onClick={onClose}>
                  Cancel
                </button>
                <button className="modal-btn primary" onClick={onConfirm}>
                  {isBooking ? 'Confirm Booking' : (isIndividualCancellation ? 'Cancel & Refund' : 'Confirm Cancellation')}
                </button>
              </>
            ) : (
              <button className="modal-btn primary" onClick={onClose}>
                Got it
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        .credit-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .credit-modal {
          background: var(--gc-bg-card);
          border: 1px solid var(--gc-border);
          border-radius: 16px;
          padding: 32px;
          max-width: 400px;
          width: 100%;
          text-align: center;
          position: relative;
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .credit-modal-close {
          position: absolute;
          top: 12px;
          right: 12px;
          background: none;
          border: none;
          color: var(--gc-text-light);
          padding: 8px;
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .credit-modal-close:hover {
          background: var(--gc-border);
          color: var(--gc-text);
        }

        .credit-modal-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }

        .credit-modal-icon.success {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
          color: #10b981;
        }

        .credit-modal-icon.confirm {
          background: linear-gradient(135deg, rgba(116, 149, 85, 0.2) 0%, rgba(116, 149, 85, 0.1) 100%);
          color: #8bab6a;
        }

        .credit-modal-icon.error {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%);
          color: #ef4444;
        }

        .credit-modal-icon.loading {
          background: linear-gradient(135deg, rgba(116, 149, 85, 0.2) 0%, rgba(116, 149, 85, 0.1) 100%);
          color: #8bab6a;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .credit-modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gc-text);
          margin-bottom: 8px;
        }

        .credit-modal-message {
          color: var(--gc-text-muted);
          font-size: 0.95rem;
          margin-bottom: 24px;
          line-height: 1.5;
        }

        .credit-modal-message.has-submessage {
          margin-bottom: 8px;
        }

        .credit-modal-submessage {
          color: var(--gc-primary);
          font-size: 0.9rem;
          margin-bottom: 24px;
          line-height: 1.4;
          font-weight: 500;
        }

        .credit-modal-balance {
          background: var(--gc-border-light);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 24px;
        }

        .balance-change {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-bottom: 8px;
        }

        .balance-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 1.25rem;
          font-weight: 700;
        }

        .balance-item.before {
          background: rgba(148, 163, 184, 0.1);
          color: var(--gc-text-muted);
        }

        .balance-item.after {
          background: rgba(116, 149, 85, 0.15);
          color: var(--gc-primary);
        }

        .balance-arrow {
          color: var(--gc-text-light);
          font-size: 1.25rem;
        }

        .balance-label {
          color: var(--gc-text-light);
          font-size: 0.85rem;
          margin: 0;
        }

        .credit-modal-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .modal-btn {
          padding: 12px 24px;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }

        .modal-btn.primary {
          background: var(--gc-gradient, #749555);
          color: white;
          flex: 1;
          max-width: 200px;
        }

        .modal-btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(116, 149, 85, 0.4);
        }

        .modal-btn.secondary {
          background: var(--gc-border);
          color: var(--gc-text-muted);
          border: 1px solid var(--gc-border);
        }

        .modal-btn.secondary:hover {
          background: var(--gc-border-light);
          color: var(--gc-text);
        }
      `}</style>
    </div>
  );
};

export default CreditActionModal;
